import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { usePosts } from '../hooks/usePosts';
import { useQueryClient } from '@tanstack/react-query';
import Toast from '../components/Toast';
import Button from '../components/Button';
import Card from '../components/Card';
import { theme } from '../theme/theme';
import { getApiClient } from '../api/apiClient';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import ImageIcon from '../components/icons/ImageIcon';
import VideoIcon from '../components/icons/VideoIcon';
import { useAddComment } from '../hooks/useAddComment';
import { usePostAiSuggestions } from '../hooks/usePostAiSuggestions';
import AiProductRecommendations, { appendProductBlock } from '../components/AiProductRecommendations';

import { formatDateTime, getPostReview } from '../utils/dateUtils';

const apiClient = getApiClient('kisanVani');

const Posts = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  // /posts?status=pending opens straight on the review queue (the notification bell links here)
  const [filter, setFilter] = useState(() => {
    const requested = new URLSearchParams(location.search).get('status');
    return ['all', 'published', 'rejected', 'pending'].includes(requested) ? requested : 'all';
  });
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    verifyPost
  } = usePosts(10, filter, null, false, page, sortBy, sortOrder);

  const [selectedPost, setSelectedPost] = useState(null);
  const [showRemarks, setShowRemarks] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const containerRef = useRef(null);
  const [pendingCommentCounts, setPendingCommentCounts] = useState({});
  const [isLoadingPendingCounts, setIsLoadingPendingCounts] = useState(false);
  const [clickedPostId, setClickedPostId] = useState(null);

  // Add Comment state for media modal - now post-specific
  const [newComment, setNewComment] = useState('');
  const [commentList, setCommentList] = useState({}); // Optimistic update - keyed by postId
  const { addComment, loading: isPostingComment } = useAddComment();
  const [showAddComment, setShowAddComment] = useState(false);
  const isSubmittingCommentRef = useRef(false);

  // AI suggestions for post-level comments
  const aiSuggestionsPostId = showAddComment && selectedPost?.postId ? selectedPost.postId : null;
  const { data: postSuggestionsData, isLoading: isLoadingPostSuggestions } = usePostAiSuggestions(aiSuggestionsPostId);
  const postAiSuggestions = postSuggestionsData?.suggestions ?? [];

  // Add Comment Handler for media modal
  const handleAddComment = async () => {
    // Synchronous guard: `isPostingComment` only updates after a re-render,
    // so a quick double-click could otherwise send the request twice.
    if (isSubmittingCommentRef.current) return;
    if (!newComment.trim()) {
      showToast('Comment cannot be empty', 'warning');
      return;
    }
    isSubmittingCommentRef.current = true;
    try {
      await addComment({ postId: selectedPost.postId, comment: newComment.trim() });
      showToast('Comment added successfully!', 'success');

      // Update comment list for the specific post
      const postId = selectedPost.postId;
      setCommentList(prev => ({
        ...prev,
        [postId]: [
          {
            comment: newComment.trim(),
            createdAt: new Date().toISOString(),
            firstName: 'You',
          },
          ...(prev[postId] || [])
        ]
      }));
      setNewComment('');
    } catch (error) {
      showToast(error.message || 'Failed to add comment.', 'error');
    } finally {
      isSubmittingCommentRef.current = false;
    }
  };

  // Cancel Add Comment handler
  const handleCancelAddComment = () => {
    setShowAddComment(false);
    setNewComment('');
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      // If we're near the bottom (within 100px) and there's more data to fetch
      if (scrollHeight - scrollTop - clientHeight < 100 && hasNextPage && !isFetchingNextPage) {
        console.log('Fetching next page...');
        fetchNextPage();
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Reset loading state when component unmounts or when navigating away
  useEffect(() => {
    return () => {
      setClickedPostId(null);
      // Clear comment state when component unmounts
      setCommentList({});
      setNewComment('');
      setShowAddComment(false);
    };
  }, []);

  // Fetch pending comment counts when posts data changes
  useEffect(() => {
    const posts = data?.pages?.[0]?.items || [];
    if (posts.length > 0) {
      const filteredPosts = posts.filter(post => {
        switch (filter) {
          case 'approved':
            return post.status === 'approved';
          case 'rejected':
            return post.status === 'rejected';
          case 'pending':
            return post.status === 'pending';
          default:
            return true; // 'all' case
        }
      });
      
      if (filteredPosts.length > 0) {
        fetchAllPendingCommentCounts(filteredPosts);
      }
    }
    
    // Clear comment state when data changes (new posts fetched)
    setCommentList({});
    setNewComment('');
    setShowAddComment(false);
    setSelectedPost(null);
  }, [data, filter]);

  const handleImageClick = (post) => {
    setSelectedPost(post);
    setShowRemarks(false);
    setRemarks('');
    // Reset comment state for new post
    setNewComment('');
    setShowAddComment(false);
  };

  const handleCommentsClick = (post) => {
    // Set loading state for the clicked post
    setClickedPostId(post.postId);
    
    // Show toast notification
    const pendingCount = pendingCommentCounts[post.postId] || 0;
    if (pendingCount > 0) {
      showToast(`Navigating to comments with ${pendingCount} pending comment${pendingCount > 1 ? 's' : ''}`, 'success');
    } else if (post.commentsCount > 0) {
      showToast(`Navigating to comments (${post.commentsCount} total)`, 'success');
    } else {
      showToast('Navigating to comments (no comments yet)', 'success');
    }
    
    // Small delay to show loading state before navigation
    setTimeout(() => {
      navigate(`/comments?postId=${encodeURIComponent(post.postId)}`, { 
        state: { 
          postId: post.postId,
          content: post.content,
          firstName: post.firstName,
          lastName: post.lastName,
          profilePic: post.profilePic,
          createdAt: post.createdAt,
          media: post.media,
          fromPosts: true
        } 
      });
    }, 150);
  };

  const handleCloseModal = () => {
    setSelectedPost(null);
    setShowRemarks(false);
    setRemarks('');
    // Reset comment state when closing modal
    setNewComment('');
    setShowAddComment(false);
  };

  const handleCloseComments = () => {
    setShowComments(false);
    setComments([]);
    setSelectedPost(null);
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Helper function to get comments for the current selected post
  const getCurrentPostComments = () => {
    if (!selectedPost) return [];
    return commentList[selectedPost.postId] || [];
  };

  // Modified Approve handler to require comment if Add Comment is open
  const handleApprove = async () => {
    if (showAddComment && !newComment.trim()) {
      showToast('Please add a comment before approving.', 'warning');
      return;
    }
    if (!selectedPost) return;
    try {
      setIsVerifying(true);
      // If comment is present, post it first
      if (showAddComment && newComment.trim()) {
        await handleAddComment();
      }
      await verifyPost(selectedPost.postId, 'published');
      handleCloseModal();
      showToast('Post approved successfully!', 'success');
    } catch (error) {
      console.error('Error approving post:', error);
      showToast('Failed to approve post. Please try again.', 'error');
    } finally {
      setIsVerifying(false);
    }
  };

  // Modified Reject handler to require comment if Add Comment is open
  const handleReject = async () => {
    if (showAddComment && !newComment.trim()) {
      showToast('Please add a comment before rejecting.', 'warning');
      return;
    }
    if (!showRemarks) {
      setShowRemarks(true);
      return;
    }
    if (!remarks.trim()) {
      showToast('Please provide remarks for rejection', 'warning');
      return;
    }
    try {
      setIsVerifying(true);
      // If comment is present, post it first
      if (showAddComment && newComment.trim()) {
        await handleAddComment();
      }
      await verifyPost(selectedPost.postId, 'rejected', remarks);
      handleCloseModal();
      showToast('Post rejected successfully!', 'success');
    } catch (error) {
      console.error('Error rejecting post:', error);
      showToast('Failed to reject post. Please try again.', 'error');
    } finally {
      setIsVerifying(false);
    }
  };

  const fetchComments = async (postId) => {
    try {
      setIsLoadingComments(true);
      const response = await apiClient.get(`/posts/comments?postId=${postId}`);
      const data = response.data;
      // Ensure data is an array
      setComments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching comments:', error);
      setComments([]); // Set empty array on error
    } finally {
      setIsLoadingComments(false);
    }
  };

  const fetchPendingCommentCount = async (postId) => {
    try {
      const response = await apiClient.get(`/posts/comments?postId=${postId}&status=all`);
      const data = response.data;
      if (data.success && data.data) {
        const pendingCount = data.data.filter(comment => comment.status === 'pending').length;
        setPendingCommentCounts(prev => ({
          ...prev,
          [postId]: pendingCount
        }));
      } else if (Array.isArray(data)) {
        // Handle case where API returns array directly
        const pendingCount = data.filter(comment => comment.status === 'pending').length;
        setPendingCommentCounts(prev => ({
          ...prev,
          [postId]: pendingCount
        }));
      }
    } catch (error) {
      console.error('Error fetching pending comments for post', postId, ':', error);
      // Set pending count to 0 on error to avoid showing undefined
      setPendingCommentCounts(prev => ({
        ...prev,
        [postId]: 0
      }));
    }
  };

  // Function to fetch pending comment counts for all posts
  const fetchAllPendingCommentCounts = async (posts) => {
    if (!posts || posts.length === 0) return;
    
    console.log('Fetching pending comment counts for', posts.length, 'posts');
    setIsLoadingPendingCounts(true);
    
    // Use Promise.allSettled to handle multiple requests concurrently
    const promises = posts.map(post => fetchPendingCommentCount(post.postId));
    
    try {
      await Promise.allSettled(promises);
      console.log('Finished fetching pending comment counts');
    } catch (error) {
      console.error('Error fetching pending comment counts:', error);
    } finally {
      setIsLoadingPendingCounts(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPage(1); // Always reset to first page on filter change
    queryClient.invalidateQueries(['posts', newFilter]);
    // Clear comment state when filter changes
    setCommentList({});
    setNewComment('');
    setShowAddComment(false);
    setSelectedPost(null);
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (data?.pages?.[0]?.currentPage < data?.pages?.[0]?.totalPages) {
      setPage((prev) => prev + 1);
      // Clear comment state when page changes
      setCommentList({});
      setNewComment('');
      setShowAddComment(false);
      setSelectedPost(null);
    }
  };
  const handlePrevPage = () => {
    if (data?.pages?.[0]?.currentPage > 1) {
      setPage((prev) => prev - 1);
      // Clear comment state when page changes
      setCommentList({});
      setNewComment('');
      setShowAddComment(false);
      setSelectedPost(null);
    }
  };
  // Sorting handler
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1); // Reset to first page on sort change
    // Clear comment state when sorting changes
    setCommentList({});
    setNewComment('');
    setShowAddComment(false);
    setSelectedPost(null);
  };

  // Flatten all pages of data (for infinite scroll, but for paged, just use first page)
  const currentPageData = data?.pages?.[0] || { items: [], currentPage: 1, totalPages: 1 };
  const filteredPosts = currentPageData.items.filter(post => {
    switch (filter) {
      case 'approved':
        return post.status === 'approved';
      case 'rejected':
        return post.status === 'rejected';
      case 'pending':
        return post.status === 'pending';
      default:
        return true; // 'all' case
    }
  });

  const showReviewColumns = ['all', 'published', 'rejected'].includes(filter);
  const reviewColumnLabel = { published: 'Approved', rejected: 'Rejected' }[filter] || 'Reviewed';
  const selectedReview = getPostReview(selectedPost);

  const formatPostMobileNumber = (post) => {
    

    if (post.countryCode && post.phone) {
      return `${post.countryCode}${post.phone}`;
      
    }

    return post.phoneNumber || post.PhoneNumber || post.phone || post.mobileNumber || post.mobile || '';
   
  };

 

  return (
    <div style={{ 
      display: 'flex', 
      minHeight: '100vh', 
      background: theme.colors.background.default,
      fontFamily: 'Poppins, sans-serif'
    }}>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <Sidebar active="posts" />
      <main 
        ref={containerRef}
        style={{ 
          flex: 1, 
          padding: `${theme.spacing.md} ${theme.spacing.xl} ${theme.spacing.xl} ${theme.spacing.xl}`,
          marginLeft: 280, 
          paddingLeft: theme.spacing.xl,
          height: '100vh',
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {isFetchingNextPage && (
          <div style={{ 
            position: 'sticky',
            bottom: 0,
            background: theme.colors.background.default,
            padding: theme.spacing.md,
            textAlign: 'center',
            color: theme.colors.text.secondary,
            zIndex: 1,
            borderTop: `1px solid ${theme.colors.grey[200]}`
          }}>
            Loading more posts...
          </div>
        )}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.sm
        }}>
          <h2 style={{ 
            ...theme.typography.h2,
            margin: 0,
            color: theme.colors.text.primary
          }}>
            Posts
          </h2>
          {(() => {
            const totalPending = Object.values(pendingCommentCounts).reduce((sum, count) => sum + count, 0);
            return (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.sm
              }}>
                {totalPending > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.xs,
                    background: theme.colors.warning.main,
                    color: '#FFFFFF',
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    borderRadius: theme.borderRadius.sm,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    animation: 'pulse 2s infinite'
                  }}>
                    <span>⏳</span>
                    <span>{totalPending} pending comment{totalPending > 1 ? 's' : ''} across all posts</span>
                  </div>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const posts = data?.pages?.[0]?.items || [];
                    if (posts.length > 0) {
                      const filteredPosts = posts.filter(post => {
                        switch (filter) {
                          case 'approved':
                            return post.status === 'approved';
                          case 'rejected':
                            return post.status === 'rejected';
                          case 'pending':
                            return post.status === 'pending';
                          default:
                            return true; // 'all' case
                        }
                      });
                      
                      if (filteredPosts.length > 0) {
                        fetchAllPendingCommentCounts(filteredPosts);
                      }
                    }
                  }}
                  disabled={isLoadingPendingCounts}
                  style={{
                    minWidth: 'auto',
                    padding: '4px 8px',
                    fontSize: '0.7rem'
                  }}
                >
                  {isLoadingPendingCounts ? '🔄' : '🔄'}
                </Button>
              </div>
            );
          })()}
        </div>
        
        {/* Filter Section */}
        <Card 
          elevation={2}
          padding={0}
          style={{
            marginBottom: theme.spacing.lg,
            position: 'sticky',
            top: 0,
            zIndex: 2,
            width: '100%'
          }}
        >
          <div style={{ 
            display: 'flex', 
            gap: theme.spacing.sm,
            alignItems: 'center',
            padding: theme.spacing.xs,
            width: '100%',
            justifyContent: 'flex-start',
            flexWrap: 'wrap'
          }}>
            <Button
              variant={filter === 'all' ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => handleFilterChange('all')}
              style={{ minWidth: '100px', margin: 0 }}
            >
              All
            </Button>
            <Button
              variant={filter === 'published' ? 'contained' : 'outlined'}
              color="success"
              onClick={() => handleFilterChange('published')}
              style={{ minWidth: '100px', margin: 0 }}
            >
              Published
            </Button>
            <Button
              variant={filter === 'rejected' ? 'contained' : 'outlined'}
              color="error"
              onClick={() => handleFilterChange('rejected')}
              style={{ minWidth: '100px', margin: 0 }}
            >
              Rejected
            </Button>
            <Button
              variant={filter === 'pending' ? 'contained' : 'outlined'}
              color="warning"
              onClick={() => handleFilterChange('pending')}
              style={{ minWidth: '100px', margin: 0 }}
            >
              Pending
            </Button>
          </div>
        </Card>

        <Card 
          elevation={1}
          padding="lg"
          style={{ flex: 1, overflow: 'auto' }}
        >
          <div style={{ maxHeight: '60vh', overflowY: 'auto', width: '100%' }}>
            {isLoading ? (
              <div style={{ 
                textAlign: 'center', 
                padding: theme.spacing.xl,
                color: theme.colors.text.secondary
              }}>
                Loading...
              </div>
            ) : error ? (
              <div style={{ 
                color: theme.colors.error.main,
                padding: theme.spacing.md
              }}>
                {error.message}
              </div>
            ) : !filteredPosts.length ? (
              <div style={{ 
                textAlign: 'center', 
                padding: theme.spacing.xl,
                color: theme.colors.text.secondary
              }}>
                No posts found
              </div>
            ) : (
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                fontFamily: theme.typography.fontFamily
              }}>
                  <thead>
                  <tr style={{ 
                    color: theme.colors.text.primary,
                    fontWeight: 500,
                    fontSize: theme.typography.body2.fontSize,
                    borderBottom: `1px solid ${theme.colors.grey[200]}`
                  }}>
                    <th style={{ textAlign: 'left', padding: theme.spacing.md, cursor: 'pointer', position: 'sticky', top: 0, background: theme.colors.background.paper, zIndex: 2, boxShadow: '0 2px 4px #0001' }} onClick={() => handleSort('createdAt')}>
                      Created Date {sortBy === 'createdAt' && (sortOrder === 'asc' ? '▲' : '▼')}
                    </th>
                    <th style={{ textAlign: 'left', padding: theme.spacing.md, cursor: 'pointer', position: 'sticky', top: 0, background: theme.colors.background.paper, zIndex: 2, boxShadow: '0 2px 4px #0001' }} onClick={() => handleSort('content')}>
                      Content {sortBy === 'content' && (sortOrder === 'asc' ? '▲' : '▼')}
                    </th>
                    <th style={{ textAlign: 'left', padding: theme.spacing.md, cursor: 'pointer', position: 'sticky', top: 0, background: theme.colors.background.paper, zIndex: 2, boxShadow: '0 2px 4px #0001' }} onClick={() => handleSort('likesCount')}>
                      Likes {sortBy === 'likesCount' && (sortOrder === 'asc' ? '▲' : '▼')}
                    </th>
                    <th style={{ textAlign: 'left', padding: theme.spacing.md, cursor: 'pointer', position: 'sticky', top: 0, background: theme.colors.background.paper, zIndex: 2, boxShadow: '0 2px 4px #0001' }} onClick={() => handleSort('commentsCount')}>
                      Comments {sortBy === 'commentsCount' && (sortOrder === 'asc' ? '▲' : '▼')}
                    </th>
                    <th style={{ textAlign: 'left', padding: theme.spacing.md, cursor: 'pointer', position: 'sticky', top: 0, background: theme.colors.background.paper, zIndex: 2, boxShadow: '0 2px 4px #0001' }} onClick={() => handleSort('viewsCount')}>
                      Views {sortBy === 'viewsCount' && (sortOrder === 'asc' ? '▲' : '▼')}
                    </th>
                    <th style={{ textAlign: 'left', padding: theme.spacing.md, cursor: 'pointer', position: 'sticky', top: 0, background: theme.colors.background.paper, zIndex: 2, boxShadow: '0 2px 4px #0001' }} onClick={() => handleSort('firstName')}>
                      Name {sortBy === 'firstName' && (sortOrder === 'asc' ? '▲' : '▼')}
                    </th>
                      {filter === 'all' && (
                      <th style={{ textAlign: 'left', padding: theme.spacing.md, cursor: 'pointer', position: 'sticky', top: 0, background: theme.colors.background.paper, zIndex: 2, boxShadow: '0 2px 4px #0001' }} onClick={() => handleSort('status')}>
                        Status {sortBy === 'status' && (sortOrder === 'asc' ? '▲' : '▼')}
                      </th>
                      )}
                      {showReviewColumns && (
                        <>
                          <th style={{ textAlign: 'left', padding: theme.spacing.md, position: 'sticky', top: 0, background: theme.colors.background.paper, zIndex: 2, boxShadow: '0 2px 4px #0001' }}>
                            {reviewColumnLabel} By
                          </th>
                          <th style={{ textAlign: 'left', padding: theme.spacing.md, position: 'sticky', top: 0, background: theme.colors.background.paper, zIndex: 2, boxShadow: '0 2px 4px #0001' }}>
                            {reviewColumnLabel} Date/Time
                          </th>
                        </>
                      )}
                    <th style={{ textAlign: 'left', padding: theme.spacing.md, position: 'sticky', top: 0, background: theme.colors.background.paper, zIndex: 2, boxShadow: '0 2px 4px #0001' }}>Media</th>
                  </tr>
                  </thead>
                  <tbody>
                    {filteredPosts.map((post, index) => {
                      const image = post.media?.find(m => m.mediaType === 'image');
                      const video = post.media?.find(m => m.mediaType === 'video');

                      let mediaThumb = (
                        <button
                          type="button"
                          aria-label="View post media"
                          onClick={() => handleImageClick(post)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 48,
                            width: 48,
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            borderRadius: theme.borderRadius.sm,
                            transition: 'box-shadow 0.2s',
                          }}
                          tabIndex={0}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleImageClick(post); }}
                        >
                          <ImageIcon size={32} color="#bdbdbd" />
                        </button>
                      );

                      if (image) {
                        mediaThumb = (
                          <button
                            type="button"
                            aria-label="View post image"
                            onClick={() => handleImageClick(post)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: 48,
                              width: 48,
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                              borderRadius: theme.borderRadius.sm,
                              transition: 'box-shadow 0.2s',
                            }}
                            tabIndex={0}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleImageClick(post); }}
                          >
                            <ImageIcon size={32} color="#90caf9" />
                          </button>
                        );
                      } else if (video) {
                        mediaThumb = (
                          <button
                            type="button"
                            aria-label="View post video"
                            onClick={() => handleImageClick(post)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: 48,
                              width: 48,
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                              borderRadius: theme.borderRadius.sm,
                              transition: 'box-shadow 0.2s',
                            }}
                            tabIndex={0}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleImageClick(post); }}
                          >
                            <VideoIcon size={32} color="#ffb74d" />
                          </button>
                        );
                      }

                      const getStatusStyle = (status) => {
                        switch (status) {
                          case 'published':
                          return { color: theme.colors.success.main, fontWeight: 500 };
                          case 'rejected':
                          return { color: theme.colors.error.main, fontWeight: 500 };
                          case 'pending':
                          return { color: theme.colors.warning.main, fontWeight: 500 };
                          default:
                          return { color: theme.colors.text.secondary };
                        }
                      };

                      return (
                      <tr 
                        key={post.postId || index}
                        style={{ 
                          borderBottom: `1px solid ${theme.colors.grey[100]}`,
                          minHeight: 50,
                          height: 50,
                          lineHeight: 2,
                          '&:hover': {
                            background: theme.colors.grey[50]
                          }
                        }}
                      >
                        <td style={{ padding: `${theme.spacing.xs} ${theme.spacing.sm}`, minHeight: 24, height: 24, lineHeight: 1.05 }}>
                          {new Date(post.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td style={{ padding: `${theme.spacing.xs} ${theme.spacing.sm}`, minHeight: 24, height: 24, lineHeight: 1.05 }}>
                          <div 
                            style={{ 
                              cursor: 'pointer',
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: theme.colors.primary.main,
                              '&:hover': {
                                textDecoration: 'underline'
                              },
                              minHeight: 24,
                              height: 24,
                              lineHeight: 1.05
                            }}
                            onClick={() => handleImageClick(post)}
                          >
                            {post.content || '-'}
                          </div>
                        </td>
                        <td style={{ padding: `${theme.spacing.xs} ${theme.spacing.sm}`, minHeight: 24, height: 24, lineHeight: 1.05 }}>{post.likesCount || 0}</td>
                        <td style={{ padding: `${theme.spacing.xs} ${theme.spacing.sm}`, minHeight: 24, height: 24, lineHeight: 1.05 }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.xs
                          }}>
                            <button
                              type="button"
                              onClick={() => handleCommentsClick(post)}
                              disabled={clickedPostId === post.postId}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  if (clickedPostId !== post.postId) {
                                    handleCommentsClick(post);
                                  }
                                }
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '4px 8px',
                                minWidth: 'auto',
                                background: clickedPostId === post.postId ? theme.colors.grey[200] : 'none',
                                border: 'none',
                                color: clickedPostId === post.postId ? theme.colors.grey[600] : theme.colors.primary.main,
                                cursor: clickedPostId === post.postId ? 'not-allowed' : 'pointer',
                                borderRadius: theme.borderRadius.sm,
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                transition: 'all 0.2s ease',
                                textDecoration: 'underline',
                                textDecorationColor: clickedPostId === post.postId ? 'transparent' : 'transparent',
                                minHeight: 'auto',
                                opacity: clickedPostId === post.postId ? 0.7 : 1
                              }}
                              title={clickedPostId === post.postId ? 'Loading...' : `View all ${post.commentsCount || 0} comment${(post.commentsCount || 0) > 1 ? 's' : ''} for this post`}
                              onMouseEnter={(e) => {
                                if (clickedPostId !== post.postId) {
                                  e.target.style.background = theme.colors.primary.light;
                                  e.target.style.textDecorationColor = theme.colors.primary.main;
                                  e.target.style.transform = 'translateY(-1px)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (clickedPostId !== post.postId) {
                                  e.target.style.background = 'none';
                                  e.target.style.textDecorationColor = 'transparent';
                                  e.target.style.transform = 'translateY(0)';
                                }
                              }}
                            >
                              {clickedPostId === post.postId ? (
                                <>
                                  <div style={{
                                    width: '12px',
                                    height: '12px',
                                    border: '1px solid #666',
                                    borderTop: '1px solid transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    marginRight: '4px'
                                  }}></div>
                                  <span>Loading...</span>
                                </>
                              ) : (
                                post.commentsCount || 0
                              )}
                            </button>
                            {isLoadingPendingCounts && pendingCommentCounts[post.postId] === undefined ? (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px',
                                background: theme.colors.grey[300],
                                color: '#666',
                                padding: '2px 6px',
                                borderRadius: theme.borderRadius.sm,
                                fontSize: '0.7rem',
                                fontWeight: 600
                              }}>
                                <div style={{
                                  width: '8px',
                                  height: '8px',
                                  border: '1px solid #666',
                                  borderTop: '1px solid transparent',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite'
                                }}></div>
                                <span>...</span>
                              </div>
                            ) : pendingCommentCounts[post.postId] > 0 ? (
                              <button
                                type="button"
                                onClick={() => handleCommentsClick(post)}
                                disabled={clickedPostId === post.postId}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    if (clickedPostId !== post.postId) {
                                      handleCommentsClick(post);
                                    }
                                  }
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px',
                                  background: clickedPostId === post.postId ? theme.colors.grey[400] : theme.colors.warning.main,
                                  color: '#FFFFFF',
                                  padding: '2px 6px',
                                  borderRadius: theme.borderRadius.sm,
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                  animation: clickedPostId === post.postId ? 'none' : 'pulse 2s infinite',
                                  cursor: clickedPostId === post.postId ? 'not-allowed' : 'pointer',
                                  border: 'none',
                                  transition: 'all 0.2s ease',
                                  minWidth: 'auto',
                                  minHeight: 'auto',
                                  opacity: clickedPostId === post.postId ? 0.7 : 1
                                }}
                                title={clickedPostId === post.postId ? 'Loading...' : `${pendingCommentCounts[post.postId]} pending comment${pendingCommentCounts[post.postId] > 1 ? 's' : ''} need${pendingCommentCounts[post.postId] > 1 ? '' : 's'} approval. Click to view comments.`}
                                onMouseEnter={(e) => {
                                  if (clickedPostId !== post.postId) {
                                    e.target.style.background = '#f57c00';
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 3px 8px rgba(0,0,0,0.3)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (clickedPostId !== post.postId) {
                                    e.target.style.background = theme.colors.warning.main;
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.2)';
                                  }
                                }}
                              >
                                {clickedPostId === post.postId ? (
                                  <>
                                    <div style={{
                                      width: '8px',
                                      height: '8px',
                                      border: '1px solid #FFFFFF',
                                      borderTop: '1px solid transparent',
                                      borderRadius: '50%',
                                      animation: 'spin 1s linear infinite'
                                    }}></div>
                                    <span>...</span>
                                  </>
                                ) : (
                                  <>
                                    <span style={{ fontSize: '0.6rem' }}>⏳</span>
                                    <span>{pendingCommentCounts[post.postId]}</span>
                                  </>
                                )}
                              </button>
                            ) : (post.commentsCount > 0 ? (
                              <button
                                type="button"
                                onClick={() => handleCommentsClick(post)}
                                disabled={clickedPostId === post.postId}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    if (clickedPostId !== post.postId) {
                                      handleCommentsClick(post);
                                    }
                                  }
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px',
                                  background: clickedPostId === post.postId ? theme.colors.grey[400] : theme.colors.success.light,
                                  color: clickedPostId === post.postId ? '#666' : theme.colors.success.dark,
                                  padding: '2px 6px',
                                  borderRadius: theme.borderRadius.sm,
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  border: `1px solid ${clickedPostId === post.postId ? theme.colors.grey[400] : theme.colors.success.main}`,
                                  cursor: clickedPostId === post.postId ? 'not-allowed' : 'pointer',
                                  transition: 'all 0.2s ease',
                                  minWidth: 'auto',
                                  minHeight: 'auto',
                                  opacity: clickedPostId === post.postId ? 0.7 : 1
                                }}
                                title={clickedPostId === post.postId ? 'Loading...' : 'All comments have been processed. Click to view comments.'}
                                onMouseEnter={(e) => {
                                  if (clickedPostId !== post.postId) {
                                    e.target.style.background = theme.colors.success.main;
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'translateY(-1px)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (clickedPostId !== post.postId) {
                                    e.target.style.background = theme.colors.success.light;
                                    e.target.style.color = theme.colors.success.dark;
                                    e.target.style.transform = 'translateY(0)';
                                  }
                                }}
                              >
                                {clickedPostId === post.postId ? (
                                  <>
                                    <div style={{
                                      width: '8px',
                                      height: '8px',
                                      border: '1px solid #666',
                                      borderTop: '1px solid transparent',
                                      borderRadius: '50%',
                                      animation: 'spin 1s linear infinite'
                                    }}></div>
                                    <span>...</span>
                                  </>
                                ) : (
                                  <>
                                    <span style={{ fontSize: '0.6rem' }}>✓</span>
                                    <span>0</span>
                                  </>
                                )}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleCommentsClick(post)}
                                disabled={clickedPostId === post.postId}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    if (clickedPostId !== post.postId) {
                                      handleCommentsClick(post);
                                    }
                                  }
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px',
                                  background: clickedPostId === post.postId ? theme.colors.grey[400] : theme.colors.grey[100],
                                  color: clickedPostId === post.postId ? '#666' : theme.colors.grey[600],
                                  padding: '2px 6px',
                                  borderRadius: theme.borderRadius.sm,
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  border: `1px solid ${clickedPostId === post.postId ? theme.colors.grey[400] : theme.colors.grey[300]}`,
                                  cursor: clickedPostId === post.postId ? 'not-allowed' : 'pointer',
                                  transition: 'all 0.2s ease',
                                  minWidth: 'auto',
                                  minHeight: 'auto',
                                  opacity: clickedPostId === post.postId ? 0.7 : 1
                                }}
                                title={clickedPostId === post.postId ? 'Loading...' : 'No comments yet. Click to view comments.'}
                                onMouseEnter={(e) => {
                                  if (clickedPostId !== post.postId) {
                                    e.target.style.background = theme.colors.grey[200];
                                    e.target.style.transform = 'translateY(-1px)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (clickedPostId !== post.postId) {
                                    e.target.style.background = theme.colors.grey[100];
                                    e.target.style.transform = 'translateY(0)';
                                  }
                                }}
                              >
                                {clickedPostId === post.postId ? (
                                  <>
                                    <div style={{
                                      width: '8px',
                                      height: '8px',
                                      border: '1px solid #666',
                                      borderTop: '1px solid transparent',
                                      borderRadius: '50%',
                                      animation: 'spin 1s linear infinite'
                                    }}></div>
                                    <span>...</span>
                                  </>
                                ) : (
                                  <>
                                    <span style={{ fontSize: '0.6rem' }}>💬</span>
                                    <span>0</span>
                                  </>
                                )}
                              </button>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: `${theme.spacing.xs} ${theme.spacing.sm}`, minHeight: 24, height: 24, lineHeight: 1.05 }}>{post.viewsCount || 0}</td>
                        <td style={{ padding: `${theme.spacing.xs} ${theme.spacing.sm}`, minHeight: 24, lineHeight: 1.2 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span>{`${post.firstName || ''} ${post.lastName || ''}`.trim() || '-'}</span>
                            {formatPostMobileNumber(post) && (
                              <span style={{ color: theme.colors.text.secondary, fontSize: '0.8rem' }}>
                                {formatPostMobileNumber(post)}
                              </span>
                            )}
                          </div>
                        </td>
                          {filter === 'all' && (
                          <td style={{ padding: `${theme.spacing.xs} ${theme.spacing.sm}`, minHeight: 24, height: 24, lineHeight: 1.05 }}>
                              <span style={getStatusStyle(post.status)}>
                                {post.status?.charAt(0).toUpperCase() + post.status?.slice(1) || '-'}
                              </span>
                            </td>
                          )}
                          {showReviewColumns && (() => {
                            const review = getPostReview(post);
                            return (
                              <>
                                <td style={{ padding: `${theme.spacing.xs} ${theme.spacing.sm}`, minHeight: 24, height: 24, lineHeight: 1.05 }}>
                                  {review.by || '-'}
                                </td>
                                <td style={{ padding: `${theme.spacing.xs} ${theme.spacing.sm}`, minHeight: 24, height: 24, lineHeight: 1.05, whiteSpace: 'nowrap' }}>
                                  {formatDateTime(review.at) || '-'}
                                </td>
                              </>
                            );
                          })()}
                        <td style={{ padding: `${theme.spacing.xs} ${theme.spacing.sm}`, minHeight: 24, height: 24, lineHeight: 1.05 }}>{mediaThumb}</td>
                      </tr>
                    );
                  })}
                  </tbody>
                </table>
            )}
          </div>
          {/* Pagination Controls */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: theme.spacing.lg, gap: theme.spacing.md }}>
            <Button onClick={handlePrevPage} disabled={currentPageData.currentPage === 1}>
              Previous
            </Button>
            <span style={{ color: theme.colors.text.secondary }}>
              Page {currentPageData.currentPage} of {currentPageData.totalPages}
            </span>
            <Button onClick={handleNextPage} disabled={currentPageData.currentPage === currentPageData.totalPages}>
              Next
            </Button>
          </div>
        </Card>

        {/* Image Modal */}
        {selectedPost && !showComments && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            fontFamily: 'Poppins, sans-serif'
          }}>
            <Card
              elevation={3}
              padding="xl"
              style={{
                maxWidth: '90vw',
              width: '90%',
              maxHeight: '90vh',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing.lg
              }}
            >
              <Button
                variant="text"
                color="primary"
                onClick={handleCloseModal}
                style={{
                  position: 'absolute',
                  top: theme.spacing.lg,
                  right: theme.spacing.lg,
                  background: 'rgba(255, 255, 255, 0.9)',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: theme.shadows.lg,
                  zIndex: 1001
                }}
              >
                ×
              </Button>
              
              <div style={{
                display: 'flex',
                gap: theme.spacing.xl,
                height: '100%',
                overflow: 'hidden'
              }}>
                {/* Media Section with Horizontal Scroll */}
                <div style={{
                  flex: '1',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  display: 'flex',
                  gap: theme.spacing.lg,
                  padding: theme.spacing.md,
                  scrollSnapType: 'x mandatory',
                  '&::-webkit-scrollbar': {
                    height: '8px'
                  },
                  '&::-webkit-scrollbar-track': {
                    background: theme.colors.grey[100],
                    borderRadius: '4px'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: theme.colors.grey[300],
                    borderRadius: '4px'
                  }
                }}>
                  {selectedPost.media?.filter(media => media.mediaType === 'image').map((media, index) => (
                    <div key={index} style={{
                      minWidth: '100%',
                      scrollSnapAlign: 'start',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: theme.spacing.md
                    }}>
                      <img
                        src={media.mediaUrl}
                        alt="Full size"
                        style={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: '60vh',
                          objectFit: 'contain',
                          borderRadius: theme.borderRadius.md
                        }}
                      />
                    </div>
                  ))}
                </div>

                {selectedPost.media?.length === 0 && (
                  <div style={{
                    // flex: '1',
                    display: 'flex',
                    justifyContent: 'start',
                    alignItems: 'start',
                    marginTop: '100px',
                    marginRight: '150px'
                  }}>
                    <p style={{
                      ...theme.typography.body1,
                      color: theme.colors.text.primary,
                      fontSize: '2rem',
                      fontWeight: 500,
                      textAlign: 'center',
                      margin: 0
                    }}>
                      No media found for this post
                    </p>
                  </div>
                )}

                {/* Stats Section */}
                <div style={{
                  width: '300px',
                  padding: theme.spacing.lg,
                  background: theme.colors.grey[50],
                  borderRadius: theme.borderRadius.md,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing.lg,
                  overflowY: 'auto'
                }}>
                  <div>
                    <p style={{
                      ...theme.typography.body1,
                      color: theme.colors.text.primary,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {selectedPost.content}
                    </p>
                  </div>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing.md
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing.sm,
                      color: theme.colors.text.secondary
                    }}>
                      <span>❤️</span>
                      <span>{selectedPost.likesCount || 0} Likes</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing.sm,
                      color: theme.colors.text.secondary
                    }}>
                      <span>👁️</span>
                      <span>{selectedPost.viewsCount || 0} Views</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing.sm,
                      color: theme.colors.text.secondary
                    }}>
                      <span>💬</span>
                      <span>{selectedPost.commentsCount || 0} Comments</span>
                    </div>
                  </div>

                  {selectedReview.label && (
                    <div style={{
                      padding: theme.spacing.md,
                      background: selectedPost.status === 'rejected' ? '#FDECEA' : '#E8F5E9',
                      borderRadius: theme.borderRadius.sm,
                      fontSize: theme.typography.body2.fontSize,
                      color: selectedPost.status === 'rejected' ? theme.colors.error.dark : theme.colors.success.dark
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{selectedReview.label} Details</div>
                      <div>{selectedReview.label} By: {selectedReview.by || '-'}</div>
                      <div>{selectedReview.label} On: {formatDateTime(selectedReview.at) || '-'}</div>
                    </div>
                  )}

              <div style={{ 
                    marginTop: 'auto',
                    paddingTop: theme.spacing.lg,
                    borderTop: `1px solid ${theme.colors.grey[200]}`
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing.md
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: theme.borderRadius.round,
                        background: theme.colors.grey[200],
                display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.colors.text.primary,
                        fontWeight: 600
                      }}>
                        {selectedPost.firstName?.charAt(0)}
                      </div>
                      <div>
                        <div style={{
                          fontWeight: 600,
                          color: theme.colors.text.primary
                        }}>
                          {`${selectedPost.firstName || ''} ${selectedPost.lastName || ''}`.trim()}
                        </div>
                        <div style={{
                          fontSize: theme.typography.body2.fontSize,
                          color: theme.colors.text.secondary
              }}>
                          {new Date(selectedPost.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Approval Actions */}
              <div style={{ 
                paddingTop: theme.spacing.lg,
                borderTop: `1px solid ${theme.colors.grey[200]}`,
                display: 'flex',
                gap: theme.spacing.md,
                flexDirection: 'column'
              }}>
                {showRemarks && (
                  <div style={{ marginBottom: theme.spacing.md }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: theme.spacing.sm,
                      ...theme.typography.body2,
                      color: theme.colors.text.primary
                    }}>
                      Rejection Remarks:
                    </label>
                    <textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Enter reason for rejection..."
                      style={{
                        width: '100%',
                        minHeight: '100px',
                        padding: theme.spacing.md,
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.colors.grey[300]}`,
                        resize: 'vertical',
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: theme.typography.body2.fontSize,
                        '&:focus': {
                          outline: 'none',
                          borderColor: theme.colors.primary.main
                        }
                      }}
                    />
                  </div>
                )}
                <div style={{ display: 'flex', gap: theme.spacing.md }}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleApprove}
                    disabled={isVerifying || isPostingComment || selectedPost?.status === 'published'}
                    fullWidth
                  >
                    {isVerifying ? 'Approving...' : 'Approve'}
                  </Button>
                  <Button
                    variant="contained"
                    color={showRemarks ? 'error' : 'warning'}
                    onClick={handleReject}
                    disabled={isVerifying || selectedPost?.status === 'rejected'}
                    fullWidth
                  >
                    {showRemarks ? 'Confirm Reject' : 'Reject'}
                  </Button>
                </div>
              </div>

              {/* Add Comment Button and Block in Media Modal */}
              <div style={{ marginTop: theme.spacing.lg }}>
                {!showAddComment ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setShowAddComment(true)}
                    style={{ minWidth: 120 }}
                  >
                    Add Comment
                  </Button>
                ) : (
                  <div style={{
                    padding: theme.spacing.lg,
                    background: theme.colors.grey[50],
                    borderRadius: theme.borderRadius.md,
                    boxShadow: theme.shadows.sm,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing.md
                  }}>
                    <label style={{
                      fontWeight: 500,
                      color: theme.colors.text.primary,
                      marginBottom: theme.spacing.sm
                    }}>
                      Add a Comment
                    </label>

                    {/* AI Suggestions */}
                    <div style={{
                      background: '#f0f9f0',
                      border: '1px solid #c8e6c9',
                      borderRadius: theme.borderRadius.sm,
                      padding: theme.spacing.md
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        marginBottom: 8,
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        color: '#2e7d32'
                      }}>
                        <span>✨</span>
                        <span>AI Suggestions</span>
                        {isLoadingPostSuggestions && (
                          <span style={{ color: '#aaa', fontWeight: 400 }}>fetching…</span>
                        )}
                      </div>
                      {postAiSuggestions.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {postAiSuggestions.map((s, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setNewComment(s)}
                              title={s}
                              style={{
                                padding: '4px 10px',
                                borderRadius: 16,
                                border: '1px solid #81c784',
                                background: '#ffffff',
                                color: '#1b5e20',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                maxWidth: 260,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      ) : !isLoadingPostSuggestions ? (
                        <div style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>
                          No AI suggestions available
                        </div>
                      ) : null}
                    </div>

                    <AiProductRecommendations
                      data={postSuggestionsData}
                      isLoading={isLoadingPostSuggestions}
                      onInsert={(block) => setNewComment(appendProductBlock(newComment, block))}
                    />

                    <textarea
                      value={newComment}
                      onChange={e => setNewComment(e.target.value)}
                      placeholder="Write your comment..."
                      style={{
                        width: '100%',
                        minHeight: 60,
                        padding: theme.spacing.md,
                        borderRadius: theme.borderRadius.sm,
                        border: `1px solid ${theme.colors.grey[300]}`,
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: theme.typography.body2.fontSize,
                        resize: 'vertical',
                        outline: 'none',
                        marginBottom: theme.spacing.sm
                      }}
                      disabled={isPostingComment}
                      maxLength={500}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.md }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddComment}
                        disabled={isPostingComment || !newComment.trim()}
                      >
                        {isPostingComment ? 'Posting...' : 'Post Comment'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleCancelAddComment}
                        disabled={isPostingComment}
                      >
                        Cancel
                      </Button>
                    </div>
                    {/* Show new comments immediately (optimistic update) */}
                    {selectedPost && commentList[selectedPost.postId] && commentList[selectedPost.postId].length > 0 && (
                      <div style={{ marginTop: theme.spacing.md }}>
                        <div style={{ fontWeight: 500, color: theme.colors.text.primary, marginBottom: theme.spacing.sm }}>
                          Recent Comments
                        </div>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {commentList[selectedPost.postId].map((c, i) => (
                            <li key={i} style={{
                              background: theme.colors.grey[100],
                              borderRadius: theme.borderRadius.sm,
                              padding: theme.spacing.sm,
                              marginBottom: theme.spacing.xs,
                              color: theme.colors.text.primary
                            }}>
                              <div style={{ fontWeight: 500 }}>{c.firstName || 'User'}</div>
                              <div style={{ fontSize: theme.typography.body2.fontSize }}>{c.comment}</div>
                              <div style={{ fontSize: '0.8em', color: theme.colors.text.secondary }}>
                                {new Date(c.createdAt).toLocaleString()}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </main>
    </div>
  );
};

export default Posts;
